import { parseMarkdown, serializeMarkdown, extractPlainText } from "@wiki/core";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Node as ProseMirrorNode, Slice } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { cx } from "../lib/cx";
import "./editor.css";
import "katex/dist/katex.min.css";
import "prosemirror-view/style/prosemirror.css";
import "prosemirror-gapcursor/style/gapcursor.css";
import "prosemirror-tables/style/tables.css";
import type { CollaborationOptions } from "./collab";
import { createEditorContext, updateEditorContext, type EditorContext } from "./context";
import { BlockHandle } from "./menus/BlockHandle";
import { FindReplacePanel } from "./menus/FindReplacePanel";
import { LinkToolbar } from "./menus/LinkToolbar";
import { PasteMenu } from "./menus/PasteMenu";
import { SelectionToolbar } from "./menus/SelectionToolbar";
import { SuggestionMenu, type SuggestionKeyHandler } from "./menus/SuggestionMenu";
import { TableToolbars } from "./menus/TableToolbars";
import { buildNodeViews } from "./nodeviews";
import {
  closeFind,
  getFindState,
  openFind,
  replaceAll as replaceAllMatches,
  replaceCurrent,
  setFind,
  stepFind,
} from "./plugins/find-replace";
import {
  anchorSelection as selectionAnchor,
  anchorTextFor,
  applyCommentMark,
  draftCommentIds,
  orderedCommentIds,
  removeCommentMark,
  setCommentMarkAttrs,
} from "./plugins/comments";
import { createSuggestionBridge } from "./plugins/suggestions";
import { createEditorState, docFromValue } from "./state";
import type {
  CommentAnchorSelection,
  EditorHandle,
  EditorValue,
  FindMatch,
  FindOptions,
  LinkOpener,
  MentionSearchHandler,
  MentionSearchResult,
  UploadHandler,
} from "./types";

export interface EditorProps {
  /** Markdown, ProseMirror JSON, or a built document. Read on mount. */
  value?: EditorValue;
  onChange?: (doc: ProseMirrorNode) => void;
  readOnly?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  maxLength?: number;
  documentId?: string;
  userId?: string;
  /** A per-user preference, not a node attribute (`editor.md` §5). */
  lineNumbers?: boolean;
  /** A per-user preference: a marker in the margin beside every anchored line. */
  commentsInGutter?: boolean;
  /** Whether a reader may still tick checkboxes in read mode (§14). */
  canToggleTasks?: boolean;
  /** The document is a template, which unlocks the placeholder mark (§3). */
  template?: boolean;
  collaboration?: CollaborationOptions;
  onUpload?: UploadHandler;
  onSearchMentions?: MentionSearchHandler;
  onCreateDocument?: (title: string) => Promise<MentionSearchResult | null>;
  onResolveDocumentUrl?: (url: string) => Promise<MentionSearchResult | null>;
  onOpenLink?: LinkOpener;
  /** `Enter` submits instead of splitting the block; see the composer. */
  onSubmit?: () => boolean;
  onCommentClick?: (commentId: string) => void;
  onCreateComment?: (selection: CommentAnchorSelection) => void;
  onNotice?: (message: string) => void;
  onOpenLightbox?: (src: string, alt: string | null) => void;
  autoFocus?: boolean;
  className?: string;
  /** The block menu is reduced to these items when given (§1). */
  allowedBlockItems?: readonly string[];
}

/**
 * The editing surface: a ProseMirror view over the `@wiki/core` schema, with
 * the menus of `docs/spec/editor.md` rendered beside it.
 *
 * The view is created once. Props that plugins and node views need are written
 * into a mutable context object rather than re-creating the view, because
 * re-creating it would lose the selection, the history and — under
 * collaboration — the Yjs binding.
 */
export const Editor = forwardRef<EditorHandle, EditorProps>(function Editor(props, ref): ReactNode {
  const {
    value,
    onChange,
    readOnly = false,
    placeholder = "",
    fullWidth = false,
    autoFocus = false,
    className,
    collaboration,
    template = false,
    allowedBlockItems,
  } = props;

  const mountRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const bridgeRef = useRef(createSuggestionBridge());
  const changeRef = useRef(onChange);
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string | null } | null>(null);

  const contextRef = useRef<EditorContext | null>(null);
  contextRef.current ??= createEditorContext({
    readOnly,
    placeholder,
    fullWidth,
    documentId: props.documentId ?? null,
    userId: props.userId ?? null,
  });
  const context = contextRef.current;

  changeRef.current = onChange;

  // Props the engine reads at event time. Written on every render so a plugin
  // asking "is this editor read-only?" during a keystroke gets today's answer.
  updateEditorContext(context, {
    readOnly,
    placeholder,
    fullWidth,
    canToggleTasks: props.canToggleTasks ?? true,
    lineNumbers: props.lineNumbers ?? false,
    commentsInGutter: props.commentsInGutter ?? false,
    documentId: props.documentId ?? null,
    userId: props.userId ?? null,
    upload: props.onUpload ?? null,
    searchMentions: props.onSearchMentions ?? null,
    createDocument: props.onCreateDocument ?? null,
    resolveDocumentUrl: props.onResolveDocumentUrl ?? null,
    onSubmit: props.onSubmit ?? null,
    onCommentClick: props.onCommentClick ?? (() => undefined),
    onCreateComment: props.onCreateComment ?? (() => undefined),
    onNotice: props.onNotice ?? (() => undefined),
    onOpenLightbox: (src, alt) => {
      setLightbox({ src, alt });
      props.onOpenLightbox?.(src, alt);
    },
    ...(props.maxLength === undefined ? {} : { maxLength: props.maxLength }),
    ...(props.onOpenLink === undefined ? {} : { openLink: props.onOpenLink }),
  });

  useLayoutEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const view = new EditorView(mount, {
      state: createEditorState({
        ...(value === undefined ? {} : { value }),
        context,
        bridge: bridgeRef.current,
        ...(collaboration === undefined ? {} : { collaboration }),
      }),
      nodeViews: buildNodeViews(context),
      // `this`, not the `view` binding: under collaboration `ySyncPlugin`
      // dispatches while the view is still being constructed.
      dispatchTransaction(this: EditorView, transaction) {
        const next = this.state.apply(transaction);
        this.updateState(next);
        setEditorState(next);
        if (transaction.docChanged) changeRef.current?.(next.doc);
      },
    });
    viewRef.current = view;
    setEditorState(view.state);
    if (autoFocus) view.focus();
    return () => {
      view.destroy();
      viewRef.current = null;
      setEditorState(null);
    };
    // The view is deliberately created once per collaboration session; every
    // other prop travels through the context object above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboration]);

  // `editable` and the surface classes come from the context, so the view has
  // to be told to re-read its props when they change.
  useEffect(() => {
    viewRef.current?.setProps({});
  }, [readOnly, fullWidth, placeholder, props.lineNumbers, props.commentsInGutter]);

  // The open suggestion menu registers its key handler here; the plugin reads
  // it out of the ref on every keystroke.
  const registerKeys = useCallback((handler: SuggestionKeyHandler) => {
    bridgeRef.current.onKeyDown = handler;
  }, []);

  const setValue = useCallback((next: EditorValue) => {
    const view = viewRef.current;
    if (!view) return;
    const doc = docFromValue(next);
    if (doc.eq(view.state.doc)) return;
    const tr = view.state.tr.replace(0, view.state.doc.content.size, new Slice(doc.content, 0, 0));
    tr.setMeta("addToHistory", false);
    view.dispatch(tr);
  }, []);

  // A `value` handed in later replaces the document — a revision being
  // previewed, a draft restored — but never while it already matches.
  useEffect(() => {
    if (value === undefined || collaboration !== undefined) return;
    setValue(value);
  }, [collaboration, setValue, value]);

  useImperativeHandle(
    ref,
    (): EditorHandle => ({
      focus: () => viewRef.current?.focus(),
      blur: () => viewRef.current?.dom.blur(),
      getMarkdown: () => (viewRef.current ? serializeMarkdown(viewRef.current.state.doc) : ""),
      getJSON: () =>
        viewRef.current
          ? (viewRef.current.state.doc.toJSON() as Record<string, unknown>)
          : { type: "doc", content: [] },
      getDoc: () => viewRef.current?.state.doc ?? docFromValue(undefined),
      getText: () => (viewRef.current ? extractPlainText(viewRef.current.state.doc) : ""),
      isEmpty: () => {
        const doc = viewRef.current?.state.doc;
        return doc === undefined || (doc.childCount <= 1 && doc.textContent === "");
      },
      insertMarkdown: (markdown: string) => {
        const view = viewRef.current;
        if (!view) return;
        const parsed = parseMarkdown(markdown);
        view.dispatch(view.state.tr.replaceSelection(new Slice(parsed.content, 0, 0)));
      },
      setMarkdown: (markdown: string) => {
        setValue(markdown);
      },
      find: (query: string, options: Partial<FindOptions> = {}): FindMatch[] => {
        const view = viewRef.current;
        if (!view) return [];
        setFind(view, { query, ...options });
        return getFindState(view.state).matches;
      },
      findNext: () => {
        if (viewRef.current) stepFind(viewRef.current, 1);
      },
      findPrevious: () => {
        if (viewRef.current) stepFind(viewRef.current, -1);
      },
      replace: (replacement: string) => {
        if (viewRef.current) replaceCurrent(viewRef.current, replacement);
      },
      replaceAll: (replacement: string) => {
        if (viewRef.current) replaceAllMatches(viewRef.current, replacement);
      },
      closeFind: () => {
        if (viewRef.current) closeFind(viewRef.current);
      },
      commentIds: () => (viewRef.current ? orderedCommentIds(viewRef.current.state.doc) : []),
      draftCommentIds: () =>
        viewRef.current ? draftCommentIds(viewRef.current.state.doc, props.userId ?? "") : [],
      anchorText: (commentId: string) =>
        viewRef.current ? anchorTextFor(viewRef.current.state.doc, commentId) : null,
      applyCommentMark: (commentId: string, options: { draft?: boolean } = {}) => {
        const view = viewRef.current;
        const userId = props.userId ?? "";
        if (!view || userId === "") return false;
        return applyCommentMark(view, {
          id: commentId,
          userId,
          ...(options.draft === undefined ? {} : { draft: options.draft }),
        });
      },
      removeCommentMark: (commentId: string) => {
        if (viewRef.current) removeCommentMark(viewRef.current, commentId);
      },
      setCommentMarkAttrs: (commentId: string, attrs: { draft?: boolean; resolved?: boolean }) => {
        if (viewRef.current) setCommentMarkAttrs(viewRef.current, commentId, attrs);
      },
      anchorSelection: (): CommentAnchorSelection =>
        viewRef.current
          ? selectionAnchor(viewRef.current.state)
          : { empty: true, from: 0, to: 0, text: "", prefix: "", suffix: "" },
    }),
    [props.userId, setValue],
  );

  // `Mod-F` inside the document opens our panel; a second press falls through
  // to the browser's own find (§11).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "f" || !(event.metaKey || event.ctrlKey)) return;
      const view = viewRef.current;
      if (view?.hasFocus() !== true) return;
      if (getFindState(view.state).open) return;
      event.preventDefault();
      openFind(view);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const view = viewRef.current;

  return (
    <div
      className={cx(
        "wiki-editor",
        fullWidth && "wiki-editor--full-width",
        readOnly && "wiki-editor--read-only",
        className,
      )}
      data-testid="editor"
    >
      <div ref={mountRef} className="wiki-editor__mount" />
      {view && editorState && (
        <>
          <FindReplacePanel view={view} state={editorState} readOnly={readOnly} />
          <SelectionToolbar view={view} state={editorState} context={context} template={template} />
          {!readOnly && (
            <>
              <SuggestionMenu
                view={view}
                state={editorState}
                context={context}
                registerKeys={registerKeys}
                {...(allowedBlockItems === undefined ? {} : { allowedItems: allowedBlockItems })}
              />
              <LinkToolbar view={view} state={editorState} context={context} />
              <TableToolbars view={view} state={editorState} context={context} />
              <PasteMenu view={view} state={editorState} />
              <BlockHandle view={view} context={context} />
            </>
          )}
        </>
      )}
      {lightbox && (
        <div
          className="wiki-lightbox"
          role="dialog"
          aria-label="Image"
          data-testid="lightbox"
          onClick={() => {
            setLightbox(null);
          }}
        >
          <img src={lightbox.src} alt={lightbox.alt ?? ""} />
        </div>
      )}
    </div>
  );
});
